import * as THREE from 'three';

/**
 * Procedural pixel-art tiles (art v2 experiment): tiny canvas textures with
 * nearest filtering, tiled across the world. Combined with the low-res
 * render target they read as hand-placed pixel art — still zero downloaded
 * assets.
 */

function makeTile(
  size: number,
  paint: (px: (x: number, y: number, color: string) => void, size: number) => void,
): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const px = (x: number, y: number, color: string): void => {
    ctx.fillStyle = color;
    ctx.fillRect(((x % size) + size) % size, ((y % size) + size) % size, 1, 1);
  };
  paint(px, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** deterministic tile noise — same tile every load */
function hash(x: number, y: number, salt: number): number {
  let h = Math.imul(x + 1, 0x27d4eb2d) ^ Math.imul(y + 1, 0x165667b1) ^ salt;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

/**
 * Grass: neutral light base (the terrain's vertex colors supply the green),
 * with speckle pixels and little two-pixel blades. Lambert multiplies
 * map x vertexColor, so this adds texture without fighting the patchwork.
 */
export function grassTile(): THREE.CanvasTexture {
  return makeTile(16, (px, size) => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const r = hash(x, y, 7);
        px(x, y, r < 0.08 ? '#ffffff' : r < 0.2 ? '#d8dcc8' : '#e9ecd9');
      }
    }
    // sparse blades: a darker stem with a light tip
    for (let i = 0; i < 7; i++) {
      const x = (hash(i, 3, 21) * size) | 0;
      const y = (hash(i, 9, 33) * size) | 0;
      px(x, y, '#c2cbae');
      px(x, y - 1, '#f6f8ec');
    }
  });
}

/** Water: banded blues with drifting light crests (offset animated per frame). */
export function waterTile(): THREE.CanvasTexture {
  return makeTile(32, (px, size) => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const band = Math.sin((y / size) * Math.PI * 4 + Math.sin(x * 0.7) * 0.8);
        px(x, y, band > 0.3 ? '#dfe8f2' : band < -0.5 ? '#b9c9de' : '#cddaea');
      }
    }
    for (let i = 0; i < 14; i++) {
      const x = (hash(i, 5, 55) * size) | 0;
      const y = (hash(i, 11, 77) * size) | 0;
      px(x, y, '#ffffff');
      px(x + 1, y, '#f2f7fc');
    }
  });
}
